/**
 * Costruzione dello snapshot condivisibile di un task (link pubblico `/#/tasks/shared/<hash>`).
 *
 * Vive fuori dal dialog perché serve anche al percorso delle menzioni
 * (`TaskDetailDialog.onComment` crea uno snapshot completo quando il commento
 * menziona qualcuno). Il server scarta comunque le chiavi fuori dalla sua
 * whitelist (`SHAREABLE_FIELDS` in backend_statistiche/app/services/task_share_service.py).
 */
import { Attachment } from "components/AttachmentList";
import { CompleteComment } from "components/CommentsSection";
import { HistoryEntry } from "components/HistoriesSection";
import {
  getLabelDataFromJsonTags,
  getParticipants,
  getDisplayName,
  getUserData,
} from "common/utilities";
import { Album } from "model/albumModel";
import { Task } from "model/activitiesModel";

export type ShareFieldKey =
  | "processmodelname"
  | "flownodename"
  | "state"
  | "priority"
  | "description"
  | "detail"
  | "labels"
  | "userdata"
  | "participants"
  | "attachments"
  | "comments"
  | "history"
  | "albums";

/** Dati che non stanno sul task ma nel dialog (già in memoria: nessun fetch). */
export interface ShareContext {
  attachments?: Attachment[];
  comments?: CompleteComment[];
  history?: HistoryEntry[];
  albums?: Album[];
  /** Solo gli album scelti a mano nella sotto-lista. `undefined` = tutti:
   *  così i chiamanti esistenti (e il preview) non cambiano. */
  albumIds?: Set<number>;
}

export interface ShareFieldDef {
  key: ShareFieldKey;
  /** Chiave i18n **già esistente** (usersmessages): il campo si traduce con
   *  quella, come nel resto dell'app. `label` è solo il fallback italiano. */
  labelKey: string;
  /** Fallback italiano; la chiave i18n vince se esiste. */
  label: string;
  /** Attivi di default: i dati di contesto del task. Spenti i "sensibili"
   *  (dati personalizzati, persone, allegati, commenti), si accendono a mano. */
  defaultOn: boolean;
  build: (task: Task, ctx: ShareContext) => unknown;
}

export const FIELD_DEFS: ShareFieldDef[] = [
  {
    key: "processmodelname",
    labelKey: "generic.model_name.caption",
    label: "Nome modello",
    defaultOn: true,
    build: (t) => t.processmodelname,
  },
  {
    key: "flownodename",
    labelKey: "generic.task",
    label: "Attività",
    defaultOn: true,
    build: (t) => t.flownodename,
  },
  {
    key: "state",
    labelKey: "pm.label.process_status",
    label: "Stato e date",
    defaultOn: true,
    build: (t) => ({
      state: t.state,
      created: t.created,
      startdoing: t.startdoing,
      done: t.done,
    }),
  },
  {
    key: "priority",
    labelKey: "generic.priorities.caption",
    label: "Priorità",
    defaultOn: true,
    build: (t) => t.priority_name,
  },
  {
    key: "description",
    labelKey: "pm.processstart.processname.label",
    label: "Nome processo",
    defaultOn: true,
    build: (t) => t.description,
  },
  {
    key: "detail",
    labelKey: "pm.startprocess.description",
    label: "Descrizione dettagliata",
    defaultOn: true,
    build: (t) => t.descriptiondetail ?? t.description_detail ?? null,
  },
  {
    key: "labels",
    labelKey: "generic.labels.caption",
    label: "Etichette",
    defaultOn: true,
    // idtag fuori: sul link pubblico non serve e non deve uscire.
    build: (t) =>
      getLabelDataFromJsonTags(t.jsontag).map((l) => ({ name: l.name, color: l.css })),
  },
  {
    key: "userdata",
    labelKey: "generic.user_data.caption",
    label: "Dati personalizzati",
    defaultOn: false,
    // Si congela la struttura così com'è (tipo incluso): la pagina pubblica la
    // rende con lo stesso `FieldsList` del dialog, quindi etichette e formati
    // (date, numeri, booleani) sono identici per costruzione.
    build: (t) => getUserData(t.userdata),
  },
  {
    key: "participants",
    labelKey: "generic.participants",
    label: "Persone coinvolte",
    defaultOn: false,
    // Solo nome e tipo: idparticipant non esce.
    build: (t) =>
      getParticipants(t.jsonparticipant)
        .map((p) => ({ name: getDisplayName(p), type: p.participanttype }))
        .filter((p) => p.name),
  },
  {
    key: "attachments",
    labelKey: "settings.attachments.label",
    label: "Allegati",
    defaultOn: false,
    /* Solo metadati: i byte NON entrano nello snapshot (sarebbero duplicazione
       di file). Li serve l'endpoint pubblico del backend, che autorizza l'id
       solo perché è in QUESTA lista congelata. `filename` è quello che
       `AttachmentList` mostra, quindi la lista è identica al dialog. */
    build: (_t, ctx) =>
      (ctx.attachments ?? []).map((a) => ({
        id: a.id,
        filename: a.filename,
        description: a.description,
        idtype: a.idtype,
        cloudlink: a.cloudlink,
        creation: a.creation,
        username: a.username,
        activityname: (a as Attachment & { activityname?: string }).activityname,
      })),
  },
  {
    key: "comments",
    labelKey: "settings.comments.label",
    label: "Commenti",
    defaultOn: false,
    /* Congelati come testo, con il minimo che `CommentsSection` legge davvero
       (verificato nel componente): `user.id` per il colore della bolla,
       `firstname`/`lastname` per iniziali e nome. Niente `picture` (è un URL
       autenticato: sul link pubblico cade sulle iniziali) e niente `username`
       (vuoto in questo DB). */
    build: (_t, ctx) =>
      (ctx.comments ?? []).map((c) => ({
        id: c.id,
        idcomment: c.idcomment,
        idflownode: c.idflownode,
        activityname: c.activityname,
        username: "",
        comment: c.comment,
        creation: c.creation,
        can_edit: false,
        edited: c.edited,
        user: {
          id: c.user?.id,
          firstname: c.user?.firstname,
          lastname: c.user?.lastname,
        },
      })),
  },
  {
    key: "history",
    labelKey: "settings.histories.label",
    label: "Storico",
    defaultOn: false,
    /* Lo storico è già la struttura che `HistoriesSection` rende nel dialog:
       si congela così com'è e la pagina pubblica lo rende con lo stesso
       componente, quindi la resa è identica per costruzione. */
    build: (_t, ctx) => ctx.history ?? [],
  },
  {
    key: "albums",
    labelKey: "album.label",
    label: "Album",
    defaultOn: false,
    /* Congela nome + immagini nell'ordine con le didascalie. `filename` serve
       alla pagina pubblica per l'etichetta; i byte li serve l'endpoint pubblico
       che autorizza l'id perché presente qui dentro (vedi task_share_service). */
    build: (_t, ctx) => {
      const byId = new Map((ctx.attachments ?? []).map((a) => [a.id, a.filename]));
      return (ctx.albums ?? [])
        .filter((al) => !ctx.albumIds || ctx.albumIds.has(al.id))
        .map((al) => ({
          name: al.name,
          images: al.images.map((im) => ({
            id: im.idupload,
            caption: im.caption,
            filename: byId.get(im.idupload) ?? "",
          })),
        }));
    },
  },
];

/** Scarta i vuoti: una sezione vuota sul link pubblico è rumore, non informazione. */
export const isEmptyValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === "" ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value as object).length === 0);

export const buildSnapshot = (
  task: Task,
  selected: Set<ShareFieldKey>,
  ctx: ShareContext
): Record<string, unknown> => {
  const fields: Record<string, unknown> = {};
  for (const def of FIELD_DEFS) {
    if (!selected.has(def.key)) continue;
    const value = def.build(task, ctx);
    if (isEmptyValue(value)) continue;
    fields[def.key] = value;
  }
  // Lo schema dei campi viaggia con i dati personalizzati: serve a `FieldsList`
  // per mostrare le stesse etichette/tipi del dialog. Non è una voce a sé
  // nella multiselect (il server lo accetta in whitelist).
  if (fields.userdata && task.userschema) {
    fields.userschema = task.userschema;
  }
  return fields;
};

/** Tutti i campi condivisibili, nessuna selezione: lo snapshot "intera task". */
export const buildFullTaskSnapshot = (
  task: Task,
  ctx: ShareContext
): Record<string, unknown> =>
  buildSnapshot(task, new Set(FIELD_DEFS.map((d) => d.key)), ctx);
